import React, { useState } from 'react';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import './RichTextEditor.css';

/**
 * Toolbar configuration for the rich text editor
 * Supports: Bold, Italic, Underline, Ordered List, Unordered List
 */
const toolbarConfig = {
  options: ['inline', 'list'],
  inline: {
    options: ['bold', 'italic', 'underline'],
    bold: { className: 'toolbar-icon' },
    italic: { className: 'toolbar-icon' },
    underline: { className: 'toolbar-icon' },
  },
  list: {
    options: ['unordered', 'ordered'],
    unordered: { className: 'toolbar-icon' },
    ordered: { className: 'toolbar-icon' },
  },
};

/**
 * RichTextEditor Component
 * A wrapper around react-draft-wysiwyg providing a controlled rich text editing experience
 * 
 * @param {Object} props
 * @param {function} props.onChange - Callback when HTML content changes (html: string) => void
 * @param {function} props.onPlainTextChange - Callback when plain text changes (text: string) => void
 * @param {number} props.maxLength - Maximum character limit for plain text (default: 5000)
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.ariaLabel - Accessibility label
 * @param {boolean} props.spellCheck - Enable browser spell checking (default: true)
 * @param {boolean} props.error - Show error styling
 * @param {string} props.initialHtml - Optional HTML string to pre-populate the editor
 */
const RichTextEditor = ({
  onChange,
  onPlainTextChange,
  maxLength = 5000,
  placeholder = 'Type your comment here...',
  ariaLabel = 'Rich text editor',
  spellCheck = true,
  error = false,
  initialHtml = '',
}) => {
  const createInitialState = (html) => {
    if (html) {
      const { contentBlocks, entityMap } = htmlToDraft(html);
      const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
      return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
  };

  const [initialState] = useState(() => createInitialState(initialHtml));
  const [editorState, setEditorState] = useState(initialState);
  const [plainTextLength, setPlainTextLength] = useState(
    () => initialState.getCurrentContent().getPlainText('').length
  );

  /**
   * Handle editor state changes
   * Enforces character limit and updates parent components
   */
  const handleEditorChange = (newEditorState) => {
    const plainText = newEditorState.getCurrentContent().getPlainText('');
    
    // Enforce character limit on plain text
    if (plainText.length <= maxLength) {
      setEditorState(newEditorState);
      setPlainTextLength(plainText.length);
      
      // Convert to HTML and notify parent
      const html = draftToHtml(convertToRaw(newEditorState.getCurrentContent()));
      
      if (onChange) {
        onChange(html);
      }
      
      if (onPlainTextChange) {
        onPlainTextChange(plainText);
      }
    }
  };

  const containerClass = `rich-text-editor-container${error ? ' error' : ''}`;
  const counterClass = `rich-text-character-count${plainTextLength >= maxLength ? ' over-limit' : ''}`;

  return (
    <div>
      <div className={containerClass}>
        <Editor
          editorState={editorState}
          onEditorStateChange={handleEditorChange}
          toolbar={toolbarConfig}
          placeholder={placeholder}
          ariaLabel={ariaLabel}
          spellCheck={spellCheck}
          stripPastedStyles={true}
        />
      </div>
    </div>
  );
};

export default RichTextEditor;
